import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Label, Card, CardHeader, CardBody } from '@repo/ui';
import { Icon } from '@repo/ui';
import { showToast } from '@repo/ui';
import { api } from '@xtnd/web/shared/api';

interface CustomFieldApplicationProps {
  fieldName: string;
  fieldId: number;
  enabled: boolean;
}

interface ProcessedMatter {
  matterId: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
  timestamp: Date;
  fieldValue?: boolean;
}

export function CustomFieldApplication({ 
  fieldName, 
  fieldId, 
  enabled 
}: CustomFieldApplicationProps) {
  const [inputText, setInputText] = useState('');
  const [processedMatters, setProcessedMatters] = useState<ProcessedMatter[]>([]);
  const [currentMatter, setCurrentMatter] = useState<string | null>(null);
  const [isLoadingOpenMatters, setIsLoadingOpenMatters] = useState(false);
  const [loadedMatterCount, setLoadedMatterCount] = useState(0);
  const PAGE_LIMIT = 200;

  // Mutation for applying custom field to a matter
  const applyFieldMutation = useMutation({
    mutationFn: async (matterId: string) => {
      const response = await api.post(`/clio/matters/${matterId}/custom-fields/${fieldId}`, {
        value: true
      });
      return response;
    },
    onError: (error: any) => {
      console.error('Failed to apply custom field:', error);
    },
  });

  // Load open/pending matters with client-driven pagination in batches of 200
  const loadAllOpenMatters = async () => {
    try {
      setIsLoadingOpenMatters(true);
      setLoadedMatterCount(0);
      setInputText('');

      let page = 1;
      let totalLoaded = 0;
      const seenIds = new Set<string>(); // Track unique IDs to detect pagination issues
       
      while (true) {
        console.log(`Loading page ${page} with limit ${PAGE_LIMIT}`);
        const endpoint = `/clio/matters/open-pending?page=${page}&limit=${PAGE_LIMIT}`;
        const response = await api.get<{ matters: Array<{ id: string; description: string }> }>(endpoint);
        const matters = response.matters || [];

        console.log(`Page ${page} returned ${matters.length} matters`);
        
        if (matters.length === 0) {
          console.log('No more matters found, breaking');
          break;
        }

        // Check for duplicates and filter them out
        const newMatters = matters.filter(m => {
          if (seenIds.has(m.id)) {
            console.warn(`Duplicate matter ID detected: ${m.id} on page ${page}`);
            return false;
          }
          seenIds.add(m.id);
          return true;
        });

        if (newMatters.length === 0) {
          console.log('All matters on this page were duplicates, breaking');
          break;
        }

        // Append IDs to the textarea
        const newIds = newMatters.map(m => m.id).join('\n');
        setInputText(prev => prev ? `${prev}\n${newIds}` : newIds);

        totalLoaded += newMatters.length;
        setLoadedMatterCount(totalLoaded);

        // If this page returned fewer than the limit, we've hit the end
        if (matters.length < PAGE_LIMIT) {
          console.log(`Page ${page} returned ${matters.length} < ${PAGE_LIMIT}, reached end`);
          break;
        }

        page += 1;
        // Yield to the UI to avoid blocking rendering
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log(`Finished loading ${totalLoaded} unique matters across ${page} pages`);
      showToast.success(`Loaded ${totalLoaded} unique open/pending matters`);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to load matters';
      showToast.error(message);
      console.error('Error loading matters:', error);
    } finally {
      setIsLoadingOpenMatters(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  };

  const processNextMatter = async (currentProcessedMatters: ProcessedMatter[] = processedMatters) => {
    // Get list of matter IDs from input text
    const matterIds = inputText
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    if (matterIds.length === 0) return;

    // Find the next unprocessed matter
    const nextMatter = matterIds.find(matterId => 
      !currentProcessedMatters.some(pm => pm.matterId === matterId)
    );

    if (!nextMatter) {
      setCurrentMatter(null);
      return;
    }

    setCurrentMatter(nextMatter);

    try {
      await applyFieldMutation.mutateAsync(nextMatter);
      const newProcessedMatter: ProcessedMatter = {
        matterId: nextMatter,
        status: 'success',
        timestamp: new Date(),
        fieldValue: true,
      };

      setProcessedMatters(prev => {
        const updated = [...prev, newProcessedMatter];
        
        // Check if there are more matters to process
        const remaining = matterIds.filter(matterId => 
          !updated.some(pm => pm.matterId === matterId)
        );

        if (remaining.length > 0) {
          // Continue processing with updated state after a small delay
          setTimeout(() => processNextMatter(updated), 500);
        } else {
          setCurrentMatter(null);
        }

        return updated;
      });

    } catch (error: any) {
      const newProcessedMatter: ProcessedMatter = {
        matterId: nextMatter,
        status: 'error',
        error: error.response?.data?.message || error.message || 'Unknown error',
        timestamp: new Date()
      };

      setProcessedMatters(prev => {
        const updated = [...prev, newProcessedMatter];
        
        // Check if there are more matters to process
        const remaining = matterIds.filter(matterId => 
          !updated.some(pm => pm.matterId === matterId)
        );

        if (remaining.length > 0) {
          // Continue processing with updated state after a small delay
          setTimeout(() => processNextMatter(updated), 500);
        } else {
          setCurrentMatter(null);
        }

        return updated;
      });
    }
  };

  const handleStartProcessing = () => {
    setProcessedMatters([]);
    setCurrentMatter(null);
    processNextMatter([]);
  };

  const handleClear = () => {
    setInputText('');
    setProcessedMatters([]);
    setCurrentMatter(null);
  };

  const handleLoadOpenMatters = () => {
    if (!isLoadingOpenMatters) {
      void loadAllOpenMatters();
    }
  };

  const getStatusIcon = (status: ProcessedMatter['status']) => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'pending':
        return '⏳';
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString();
  };

  const isProcessing = !!currentMatter;
  const hasMattersToProcess = inputText.trim().length > 0;

  if (!enabled) {
    return (
      <Card className="border-gray-300 dark:border-gray-600 opacity-50">
        <CardHeader>
          <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400">
            Apply Custom Field to Matters
          </h3>
        </CardHeader>
        <CardBody>
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              The custom field "{fieldName}" must be created first before it can be applied to matters.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="border-xtnd-light">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
              Apply Custom Field to Matters
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Apply "{fieldName}" to all open and pending matters
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadOpenMatters}
            disabled={isLoadingOpenMatters || isProcessing}
            className="border-xtnd-blue text-xtnd-blue hover:bg-xtnd-blue hover:text-white"
          >
            <Icon name="HiClipboardList" className={`w-4 h-4 mr-2 ${isLoadingOpenMatters ? 'animate-spin' : ''}`} />
            {isLoadingOpenMatters ? 'Loading...' : 'Load Open Matters'}
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Input Section */}
          <Card className="border-gray-200 dark:border-gray-700">
            <CardHeader>
              <h4 className="text-md font-medium text-xtnd-dark dark:text-xtnd-white">Matter IDs</h4>
            </CardHeader>
            <CardBody className="space-y-4">
              <div>
                <Label htmlFor="matterIds">Enter Matter IDs (one per line)</Label>
                <textarea
                  id="matterIds"
                  value={inputText}
                  onChange={handleInputChange}
                  placeholder="Enter Clio Matter IDs, one per line"
                  className="w-full h-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-xtnd-dark-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-xtnd-blue focus:border-transparent"
                />
                {isLoadingOpenMatters && (
                  <div className="mt-2 text-xs text-xtnd-dark dark:text-gray-300">
                    Loading Open Matters... <span className="font-semibold">{loadedMatterCount}</span> loaded
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use the "Load Open Matters" button above to automatically populate this field
                </p>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="primary"
                  onClick={handleStartProcessing}
                  disabled={!hasMattersToProcess || isProcessing || isLoadingOpenMatters}
                  className="bg-xtnd-blue hover:bg-xtnd-blue/90"
                >
                  <Icon name="HiPlay" size="sm" className="mr-2" />
                  {isProcessing ? 'Processing...' : 'Start Processing'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleClear}
                  disabled={isProcessing}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Clear
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Results Section */}
          <Card className="border-gray-200 dark:border-gray-700">
            <CardHeader>
              <h4 className="text-md font-medium text-xtnd-dark dark:text-xtnd-white">Processing Results</h4>
            </CardHeader>
            <CardBody>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {currentMatter && (
                  <div className="flex items-center space-x-2 text-sm">
                    <span>⏳</span>
                    <span className="text-xtnd-blue dark:text-xtnd-blue-300">
                      Applying custom field to matter {currentMatter}...
                    </span>
                  </div>
                )}
                {processedMatters.map((matter, index) => (
                  <div key={`${matter.matterId}-${index}`} className="flex items-start space-x-2 text-sm">
                    <span>{getStatusIcon(matter.status)}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className={matter.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}>
                          Matter {matter.matterId}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">
                          {formatTimestamp(matter.timestamp)}
                        </span>
                      </div>
                      {matter.error && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {matter.error}
                        </p>
                      )}
                      {matter.status === 'success' && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Custom field applied successfully
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {!currentMatter && processedMatters.length === 0 && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No matters processed yet
                  </div>
                )}
              </div>
              
              {/* Summary */}
              {processedMatters.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-medium text-gray-700 dark:text-gray-300">Total</div>
                      <div className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                        {processedMatters.length}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-green-600 dark:text-green-400">Success</div>
                      <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {processedMatters.filter(m => m.status === 'success').length}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-red-600 dark:text-red-400">Failed</div>
                      <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                        {processedMatters.filter(m => m.status === 'error').length}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </CardBody>
    </Card>
  );
}
